import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAlternateRoute } from '../utils/routeMap';

const logoText = '/images/logo.webp';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Check if we are on a page that should have a transparent header initially
  const isTransparentHeader = ['/', '/hakkimizda', '/hizmetlerimiz', '/projelerimiz', '/blog', '/iletisim', '/sss'].includes(location.pathname) || location.pathname.startsWith('/projelerimiz/');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleContactClick = () => {
    navigate('/iletisim');
    setIsOpen(false);
  };

  const changeLanguage = (lang: string) => {
    const currentPath = location.pathname;
    
    if (lang === 'en' && !currentPath.startsWith('/en')) {
      const newPath = getAlternateRoute(currentPath, 'en');
      i18n.changeLanguage('en');
      navigate(newPath);
    } else if (lang === 'tr' && currentPath.startsWith('/en')) {
      const newPath = getAlternateRoute(currentPath, 'tr');
      i18n.changeLanguage('tr');
      navigate(newPath);
    }
    setIsOpen(false);
  };

  const { t } = useTranslation();

  const getLocalizedNavItems = () => {
    const isEn = i18n.language === 'en';
    return [
      { label: t('nav.home'), path: isEn ? '/en/' : '/' },
      { label: t('nav.about'), path: isEn ? '/en/about' : '/hakkimizda' },
      { label: t('nav.services'), path: isEn ? '/en/services' : '/hizmetlerimiz' },
      { label: t('nav.projects'), path: isEn ? '/en/projects' : '/projelerimiz' },
      // { label: 'Demolar', path: isEn ? '/en/demos' : '/demolar' }, // Canlıya alınacağı zaman açılacak
      { label: t('nav.blog'), path: isEn ? '/en/blog' : '/blog' },
      { label: t('nav.contact'), path: isEn ? '/en/contact' : '/iletisim' },
    ];
  };

  const navItems = getLocalizedNavItems();

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${scrolled || !isTransparentHeader ? 'bg-brand-dark/95 backdrop-blur-md shadow-lg py-4' : 'bg-transparent py-6'
        }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <NavLink to={i18n.language === 'en' ? '/en/' : '/'} className="flex items-center">
            <img
              src={logoText}
              alt="Pato Yazılım Logo"
              className="h-10 md:h-12 w-auto object-contain"
              width="180"
              height="48"
            />
          </NavLink>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/' || item.path === '/en/'}
                className={({ isActive }) =>
                  `text-lg font-bold ${isActive
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-purple-400'
                    : 'text-gray-300 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-brand-blue hover:to-purple-400'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* CTA & Language */}
          <div className="hidden lg:flex items-center gap-6">
            <div className="hidden items-center gap-2 font-bold">
              <button 
                onClick={() => changeLanguage('tr')} 
                className={`${i18n.language === 'tr' ? 'text-brand-blue' : 'text-gray-400 hover:text-white'} transition-colors`}
              >
                TR
              </button>
              <span className="text-gray-600">|</span>
              <button 
                onClick={() => changeLanguage('en')} 
                className={`${i18n.language === 'en' ? 'text-brand-blue' : 'text-gray-400 hover:text-white'} transition-colors`}
              >
                EN
              </button>
            </div>
            
            <button
              onClick={handleContactClick}
              className="bg-brand-blue hover:bg-blue-600 text-white px-7 py-3 rounded-full font-bold transition-all shadow-lg hover:shadow-brand-blue/30 transform hover:-translate-y-0.5"
            >
              {t('common.contactUs')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            aria-label="Toggle mobile menu"
            className={`lg:hidden p-2 -mr-2 focus:outline-none transition-colors duration-300 ${isOpen ? 'text-brand-blue' : 'text-white'}`}
          >
            {isOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden fixed top-[72px] md:top-[80px] left-0 w-full h-[calc(100vh-72px)] md:h-[calc(100vh-80px)] bg-brand-dark border-t border-white/10 transition-all duration-300 overflow-y-auto ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
      >
        <div className="flex flex-col p-6 gap-6">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/' || item.path === '/en/'}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `text-xl font-bold transition-all duration-300 block w-full py-3 border-b border-white/5 ${isActive
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-purple-400'
                  : 'text-gray-300 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-brand-blue hover:to-purple-400'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <button
            onClick={handleContactClick}
            className="bg-brand-blue text-white px-6 py-4 rounded-lg font-bold mt-4 w-full text-lg"
          >
            {t('common.contactUs')}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;